import { useCallback, useDeferredValue, useEffect, useMemo, useState, useSyncExternalStore, useTransition } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from '@app/utils/redux'

import type { TrackableValue } from '@app/packages/reactives/TrackableValue'
import { unwrapResult } from '@app/packages/Result/Result'

import { useEvent } from '@app/hooks/useEvent'

import { createThunk } from '@app/store/thunk'

import { asError, noop, useStackContext } from './shared'
import type { ThunkSelector } from './thunkSelector'
import { CircularThunkError, LoadRequest } from './thunkSelector'

/**
 * Consume via `.suspend()` inside `<ErrorBoundary>` + `<Suspense>`, or `.spawn()` to branch on state
 * without suspending — both dispatch soft refresh requests. Top-level `value`/`loading`/`error` don't.
 */
export const useThunkSelector = <T>(selector: ThunkSelector<T>) => {
  const dispatch = useAppDispatch()
  const rawState = useSelector(selector)
  const state = useDeferredValue(rawState)
  const treeStack = useStackContext()
  const [retryCount, setRetryCount] = useState(0)
  const [, start] = useTransition()

  const type: 'error' | 'empty' | 'loaded' = state.error ? 'error' : state.value.value instanceof LoadRequest ? 'empty' : 'loaded'
  const stateError = state.error ? state.value : null
  const loadRecipe = !state.error && state.value.value instanceof LoadRequest ? state.value.value.action : null
  const load = loadRecipe ? dispatch(loadRecipe) : null

  const loading = useMaybeTrackable(load?.loading) ?? false
  const loadError = useMaybeTrackable(load?.error)
  const error = useMemo(() => (stateError ? asError(stateError) : loadError), [loadError, stateError])

  const value = state.error ? null : state.value.value instanceof LoadRequest ? null : state.value.value

  const check = useEvent((id: string, hash: string) =>
    createThunk(async (dispatch, getState) => {
      const state = unwrapResult(selector(getState()))
      if (state.value instanceof LoadRequest) {
        const data = dispatch(state.value.action)
        if (data.id === id && data.hash === hash) {
          throw new CircularThunkError(`State loaded yet empty: ${dispatch(state.value.action).id}`)
        }
      }
    })
  )

  const retry = useEvent(() => {
    const load = loadRecipe ? dispatch(loadRecipe) : null
    load?.abort()
    start(() => {
      setRetryCount(v => (v + 1) & 0xffff)
    })
  })

  // The only entry point that dispatches soft requests — hence value/error/loading here, so branching
  // on state cannot skip them. Failures surface as `error` and rethrow from suspend()/map(), not here.
  const spawn = useCallback((): SpawnedState<T> => {
    if (state.error) return failed(asError(state.value), state.value, retry)
    state.value.softRequests
      .map(r => dispatch(r))
      .forEach(s => {
        void s.promise()
      })
    const val = state.value.value
    if (val instanceof LoadRequest) {
      const load = dispatch(val.action)
      const loadError = load.error.value
      if (loadError) return failed(loadError, loadError, retry)
      const p = load.promise(check)
      return { suspend: () => treeStack.suspend(p), map: () => ({ suspend: () => treeStack.suspend(p) }), value: null, error: null, loading: true, retry }
    }
    return { suspend: () => val, map: <V>(m: (v: T) => V) => ({ suspend: () => m(val) }), value: val, error: null, loading: false, retry }
  }, [check, dispatch, retry, state.error, state.value, treeStack])

  const suspend = useCallback(() => {
    return spawn().suspend()
  }, [spawn])

  const loadTrigger = [load?.hash, retryCount].join(':')

  const update = useEvent(() => {
    if (!load) return

    void treeStack.handleAction(load.promise(check))
  })

  useEffect(() => {
    update()
  }, [loadTrigger, treeStack, update])

  return {
    type,
    spawn,
    suspend,
    value,
    loading,
    error,
    retry,
  }
}

export type SpawnedState<T> = {
  suspend: () => T
  map: <V>(m: (v: T) => V) => { suspend: () => V }
  value: T | null
  error: Error | null
  loading: boolean
  retry: () => void
}

const failed = <T>(error: Error, thrown: unknown, retry: () => void): SpawnedState<T> => {
  const rethrow = (): never => {
    throw thrown
  }
  return { suspend: rethrow, map: () => ({ suspend: rethrow }), value: null, error, loading: false, retry }
}

/**
 * `spawn()` drives the reactive graph from inside render, so the listener fires mid-render — a plain
 * setValue there is a React warning and an update React may drop. useSyncExternalStore is the only
 * bridge that tolerates a store notifying during render.
 */
const useMaybeTrackable = <T>(val: TrackableValue<T> | undefined) => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!val) return noop
      const subscription = val.listen(onStoreChange)
      return () => subscription[Symbol.dispose]()
    },
    [val]
  )

  // Stable per value: ReactiveValue.update skips equal writes, so the snapshot only moves on a real change.
  const getSnapshot = useCallback(() => val?.value ?? null, [val])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
